// DisqualifyModal — confirmation + reason-picker that pops when a rep
// moves a company into a "graveyard" bucket flagged with requires_reason.
//
// Reasons come from /api/lost-reasons (same table that powers the deal
// closed-lost flow). Each reason has an `auto_dnc` flag — when picked,
// the company AND every linked lead get added to DNC on save.
//
// Props:
//   company: { id, name }
//   bucket:  { id, label, color, emoji }
//   reasons: list from api.listLostReasons()
//   onSaved(result): result = { auto_dnc_applied, reason_label, ... }
//   onCancel()

import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';

export default function DisqualifyModal({ company, bucket, reasons = [], onSaved, onCancel }) {
  const [reasonId, setReasonId] = useState('');
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const chosen = useMemo(() => reasons.find(r => r.id === reasonId), [reasons, reasonId]);
  const willDnc = !!chosen?.auto_dnc;

  // Default to the first reason so the user can hit Save immediately if
  // they don't care which bucket — but only after reasons load.
  useEffect(() => {
    if (!reasonId && reasons.length) setReasonId(reasons[0].id);
  }, [reasons, reasonId]);

  // ESC closes the modal, Enter saves (matches the rest of the app).
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter' && !saving && reasonId) handleSave();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonId, saving]);

  async function handleSave() {
    if (!reasonId) {
      setError('Pick a reason before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.disqualifyCompany(company.id, {
        bucket_id: bucket.id,
        reason_id: reasonId,
        note,
      });
      onSaved?.(result);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-slate-800 border border-slate-600 rounded-xl w-full max-w-md p-6"
      >
        <h3 className="text-lg font-bold text-slate-100 mb-1">
          Move <span className="text-indigo-300">{company.name}</span> to
          {' '}
          <span style={{ color: bucket.color || '#ef4444' }}>
            {bucket.emoji ? `${bucket.emoji} ` : ''}{bucket.label}
          </span>
        </h3>
        <div className="text-slate-500 text-xs mb-4">
          Pick a reason so the funnel knows why this didn't fit. Reasons flagged
          for auto-DNC will also suppress every lead linked to this company from
          future outreach.
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Reason *</label>
            <select
              value={reasonId}
              onChange={e => setReasonId(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none"
            >
              <option value="">— pick a reason —</option>
              {reasons.map(r => (
                <option key={r.id} value={r.id}>
                  {r.label}{r.auto_dnc ? ' · auto-DNC' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-400 block mb-1">Note (optional)</label>
            <textarea
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Anything else worth recording — kept on the company"
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none resize-none"
            />
          </div>

          {willDnc && (
            <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3 text-xs text-red-300">
              <strong>Heads up:</strong> "{chosen?.label}" is flagged auto-DNC. Saving will
              also add {company.name}{' '}
              {company.contact_count > 0
                ? <>and <strong>{company.contact_count}</strong> linked contact{company.contact_count === 1 ? '' : 's'} </>
                : 'and every lead linked to it '}
              to the Do-Not-Contact list.
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-2 text-xs text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !reasonId}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50"
          >{saving ? 'Saving…' : willDnc ? 'Disqualify + DNC' : 'Disqualify'}</button>
        </div>
      </div>
    </div>
  );
}
